import { useState } from 'react';
import { Loader2, Play, Search, CheckCircle2, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function PassBadge({ pass: ok, reason }) {
  if (ok) {
    return (
      <Badge className="bg-green-600 hover:bg-green-700 text-white gap-1 text-[10px]">
        <CheckCircle2 className="h-3 w-3" />
        PASS
      </Badge>
    );
  }
  return (
    <Badge variant="destructive" className="gap-1 text-[10px]" title={reason}>
      <XCircle className="h-3 w-3" />
      FAIL
    </Badge>
  );
}

export function TrueLayerRegistryAuditPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.truelayerRegistryAudit();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Search className="h-5 w-5 text-blue-600" />
            TrueLayer Registry Audit
          </CardTitle>
          <Button onClick={run} disabled={loading} size="sm">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            Audit
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          Audits each OB registry entry against the live TrueLayer providers list. Shows the matched provider_id, match count, and pass/fail per entry. Read-only.
        </p>
      </CardHeader>
      {result && (
        <CardContent className="space-y-4">
          {/* Summary banner */}
          <div className={`border rounded-lg p-3 ${result.all_pass ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800'}`}>
            <div className="flex items-center gap-2 text-sm font-medium">
              {result.all_pass ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span className="text-green-700 dark:text-green-400">All registry entries resolved ({(result.rows || []).length} entries, {result.providers_total} providers)</span>
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4 text-red-600" />
                  <span className="text-red-700 dark:text-red-400">
                    {(result.rows || []).filter(r => !r.pass).length} of {(result.rows || []).length} entries failed resolution
                    {result.providers_error && ` (providers fetch error: ${result.providers_error})`}
                  </span>
                </>
              )}
            </div>
            {result.is_sandbox && (
              <p className="text-xs text-muted-foreground mt-1">Sandbox mode: all entries resolve to mock provider. Run from production for the real audit.</p>
            )}
          </div>

          {/* Registry table */}
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left px-3 py-1.5 font-medium">Registry ID</th>
                  <th className="text-left px-3 py-1.5 font-medium">Name</th>
                  <th className="text-left px-3 py-1.5 font-medium">Match Pattern</th>
                  <th className="text-left px-3 py-1.5 font-medium">Resolved Provider</th>
                  <th className="text-left px-3 py-1.5 font-medium">Matches</th>
                  <th className="text-left px-3 py-1.5 font-medium">Status</th>
                  <th className="text-left px-3 py-1.5 font-medium">Reason</th>
                </tr>
              </thead>
              <tbody>
                {(result.rows || []).map((row) => (
                  <tr key={row.registry_id} className="border-b last:border-0">
                    <td className="px-3 py-1.5 font-mono font-medium">{row.registry_id}</td>
                    <td className="px-3 py-1.5">{row.registry_name}</td>
                    <td className="px-3 py-1.5 font-mono text-muted-foreground">
                      {row.provider_match || '(exact id)'}
                    </td>
                    <td className="px-3 py-1.5 font-mono">
                      {row.resolved_provider_id || '--'}
                      {row.live_connection_provider && (
                        <div className="text-[10px] text-blue-600 font-medium mt-0.5">
                          live: {row.live_connection_provider}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-1.5">{row.match_count}</td>
                    <td className="px-3 py-1.5">
                      <PassBadge pass={row.pass} reason={row.reason} />
                    </td>
                    <td className="px-3 py-1.5 text-[10px] text-muted-foreground max-w-[250px]">
                      {row.reason}
                      {row.candidates && row.candidates.length > 0 && !row.pass && (
                        <div className="mt-1">
                          <span className="font-medium">Candidates: </span>
                          {row.candidates.map(c => c.provider_id).join(', ')}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Consistency check */}
          {result.consistency && (
            <div className="text-xs text-muted-foreground">
              Env/URL consistency: {result.consistency.pass ? 'PASS' : 'FAIL'} ({result.consistency.reason})
            </div>
          )}

          {result.checked_at && (
            <p className="text-[10px] text-muted-foreground text-right">
              Checked at {new Date(result.checked_at).toLocaleString()}
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
}
